/**
 * Two-factor authentication and the sessions signed in as you.
 *
 * Both need the password again before they will do anything, which is
 * deliberate: someone who walks up to an unlocked laptop should not be able to
 * turn off two-factor or read the enrolment secret.
 */
import { useState } from 'react';
import {
  Alert, Badge, Button, Card, Code, Group, Image, Loader, PasswordInput, Stack, Table, Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as gw from '../../api/gateway';
import { revokeSession, sessions, totpDisable, totpShowQr, totpStatus } from '../../api/gateway';
import { formatDateTime } from '../../theme';
import { describeDevice } from './userAgent';

export function SecurityTab() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['totp'], queryFn: totpStatus });
  const list = useQuery({ queryKey: ['sessions'], queryFn: sessions });

  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<gw.TotpQr | null>(null);
  const [showing, setShowing] = useState<'none' | 'qr' | 'disable'>('none');

  const reveal = useMutation({
    mutationFn: () => totpShowQr(password),
    onSuccess: (r) => {
      setQr(r);
      setPassword('');
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not show the code', message: (e as Error).message }),
  });

  const disable = useMutation({
    mutationFn: () => totpDisable(password, code),
    onSuccess: () => {
      setShowing('none');
      setPassword('');
      setCode('');
      qc.invalidateQueries({ queryKey: ['totp'] });
      notifications.show({
        color: 'yellow',
        title: 'Two-factor turned off',
        message: 'Your account is now protected by its password alone.',
      });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not turn it off', message: (e as Error).message }),
  });

  const revoke = useMutation({
    mutationFn: revokeSession,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      notifications.show({ color: 'teal', title: 'Signed out', message: 'That session has been ended.' });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not sign it out', message: (e as Error).message }),
  });

  const enabled = status.data?.enabled ?? false;
  /* Until the answer lands, say nothing rather than "Off". Guessing here is
     the same mistake the sessions table made below: a confident wrong answer
     about your own security settings is worse than a moment of blank. */
  const settled = !status.isPending;

  return (
    <Stack>
      <Card withBorder radius="md" padding="lg">
        <Stack>
          <Group gap="xs">
            <Text fw={600}>Two-factor authentication</Text>
            {settled ? (
              <Badge color={enabled ? 'teal' : 'chartAmber'} variant="light">
                {enabled ? 'On' : 'Off'}
              </Badge>
            ) : (
              <Loader size="xs" />
            )}
          </Group>
          {!settled ? (
            <Text size="sm" c="dimmed">Checking.</Text>
          ) : enabled ? (
            <Text size="sm" c="dimmed">
              Turned on {status.data?.enrolled_at ? formatDateTime(status.data.enrolled_at) : ''}.
              You need a code from your authenticator app each time you sign in.
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              Your account is protected by its password alone.
            </Text>
          )}

          {showing === 'none' && (
            <Group>
              {settled && enabled && (
                <>
                  <Button variant="light" onClick={() => { setQr(null); setShowing('qr'); }}>
                    Show my setup code
                  </Button>
                  <Button variant="subtle" color="red" onClick={() => setShowing('disable')}>
                    Turn off
                  </Button>
                </>
              )}
            </Group>
          )}

          {showing === 'qr' && (
            <Stack>
              <Alert color="gray" variant="light">
                <Text size="sm">
                  This is the same code you scanned when you set it up, for adding another
                  device. Anyone who photographs it can sign in as you, so treat it like the
                  password itself.
                </Text>
              </Alert>
              {qr ? (
                <Stack align="center">
                  {qr.qr && <Image src={qr.qr} w={200} h={200} alt="Setup code" />}
                  {qr.secret && <Code>{qr.secret}</Code>}
                  <Button variant="subtle" onClick={() => { setQr(null); setShowing('none'); }}>
                    Done
                  </Button>
                </Stack>
              ) : (
                <>
                  <PasswordInput
                    label="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                  />
                  <Group justify="flex-end">
                    <Button variant="subtle" onClick={() => { setPassword(''); setShowing('none'); }}>
                      Cancel
                    </Button>
                    <Button loading={reveal.isPending} disabled={!password} onClick={() => reveal.mutate()}>
                      Show it
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          )}

          {showing === 'disable' && (
            <Stack>
              <Alert color="chartAmber" variant="light">
                <Text size="sm">
                  Your organisation may require two-factor, in which case you will be asked to
                  set it up again at your next sign in.
                </Text>
              </Alert>
              <PasswordInput
                label="Your password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
              <TextInput
                label="Code from your app"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value.replace(/[^0-9]/g, ''))}
              />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => { setPassword(''); setCode(''); setShowing('none'); }}>
                  Cancel
                </Button>
                <Button
                  color="red"
                  loading={disable.isPending}
                  disabled={!password || code.length < 6}
                  onClick={() => disable.mutate()}
                >
                  Turn off two-factor
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Stack>
          <Text fw={600}>Where you are signed in</Text>
          <Text size="sm" c="dimmed">
            Every browser and device holding a session. Ending one signs it out immediately.
          </Text>
          {/*
            Signed in and Expires, not "last seen": the gateway does not record
            activity on a session, only when it began and when it stops working.
            This column used to read a `last_seen_at` that no endpoint has ever
            sent, so it showed a dash on every row and looked like a device that
            had never been used.
          */}
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={250}>Device</Table.Th>
                <Table.Th>Address</Table.Th>
                <Table.Th>Signed in</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.isPending && (
                <Table.Tr><Table.Td colSpan={5}>
                  <Group justify="center" py="lg"><Loader size="sm" /></Group>
                </Table.Td></Table.Tr>
              )}
              {(list.data?.sessions ?? []).map((s) => (
                <Table.Tr key={s.public_id}>
                  <Table.Td>
                    {/* Wrapping, not nowrap: with both on one line the column
                        is a few pixels short of "Chrome on macOS" plus the
                        badge, and whichever the browser chooses to squeeze
                        ends up as an ellipsis. Letting the badge drop to a
                        second line means neither is ever cut off, however long
                        a device name the fallback produces. */}
                    <Group gap="xs">
                      <Text size="sm" lineClamp={1} title={s.user_agent ?? undefined} style={{ minWidth: 0 }}>
                        {describeDevice(s.user_agent)}
                      </Text>
                      {s.is_current && (
                        <Badge size="xs" variant="light" color="teal" style={{ flexShrink: 0 }}>
                          This one
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm">{s.ip_address || '-'}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm">{s.created_at ? formatDateTime(s.created_at) : '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {s.expires_at ? formatDateTime(s.expires_at) : '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td w={110}>
                    {/* The gateway refuses to revoke the caller's own session,
                        so offering the button here would only ever error. */}
                    {!s.is_current && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        loading={revoke.isPending && revoke.variables === s.public_id}
                        onClick={() => revoke.mutate(s.public_id)}
                      >
                        Sign out
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {!list.isPending && (list.data?.sessions ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="dimmed">No other sessions.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Stack>
      </Card>
    </Stack>
  );
}
